import { noop } from 'lodash-es';
import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';
import { BoostIconDisplay } from 'src/components/SponsoredListings';
import { Content } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { EmailNavPageNameHandle } from 'src/navigations/Routes/Email';
// Eager load: Core routes (frequently accessed)
import { Home } from 'src/navigations/Routes/Home/Home';
import { Inventory } from 'src/navigations/Routes/Inventory';
// Eager load: Login/Auth routes (always needed immediately)
import {
  Login,
  LoginCallback,
  LoginFailure,
  Logout,
} from 'src/navigations/Routes/Login';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { SettingTab } from 'src/navigations/Routes/Settings/constants';
import { SaleAlertIcon } from 'src/svgs';
import { PaymentsIcon } from 'src/svgs/PaymentsIcon';
import { ReportsIcon } from 'src/svgs/ReportsIcon';
import {
  CartOutlineIcon,
  ChatIcon,
  HomeOutlineIcon,
  IconsProps,
  PaperTicketsOutlineIcon,
  PlusIcon,
  SearchSolidIcon,
  SellerTraderOutlineIcon,
  SellOutlineIcon,
  SettingsIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { DeviceAwareContentWrapper } from 'src/utils/contentUtils';
import { withSuspense } from 'src/utils/lazyWithRetry';
import { RootErrorPage } from 'src/views';
import { CommandOptionType } from 'src/WebApiController';
import { UserSetting } from 'src/WebApiController';

import App from './App';
import {
  LazyAccounting,
  LazyActivity,
  LazyCustomTabEmbedView,
  LazyEmail,
  LazyHelpCenter,
  LazyInventoryEvent,
  LazyInventoryEventPageRedirect,
  LazyInventorySearch,
  LazyInventorySearchV2,
  LazyNotifications,
  LazyOnSaleDiscovery,
  LazyOnSaleEvent,
  LazyPayments,
  LazyPurchaseEvent,
  LazyPurchases,
  LazyPurchaseSearch,
  LazyPurchaseSearchV2,
  LazyReachProSupport,
  LazyRedirectToStubHubUk,
  LazyReleaseNotes,
  LazyReports,
  LazyReportsCommission,
  LazyReportsCostOfGoodsSold,
  LazyReportsInventory,
  LazyReportsInventoryV2,
  LazyReportsPurchase,
  LazyReportsPurchaseOrderLineItem,
  LazyReportsSale,
  LazyReportsSaleCostAdjustments,
  LazyReportsSaleLineItem,
  LazyReportsSaleV2,
  LazyReportsSoldTicket,
  LazyReportsTicketGroup,
  LazyReportsV2,
  LazyRetail,
  LazySales,
  LazySaleSearch,
  LazySaleSearchV2,
  LazySalesEvent,
  LazySettings,
  LazySettingsViews,
  LazySponsoredListings,
  LazySyncCenter,
  LazyTicketUtilsLanding,
} from './AppRoutes.lazy';

export type AppRouteConfigType = {
  favorite?: UserSetting;
  view: MainRoute;
  path: string;
  label: React.ReactNode;
  icon: (props: IconsProps) => JSX.Element;
  subRoutes?: Record<
    string,
    {
      Path: string;
      Icon: (props: IconsProps) => JSX.Element;
    }
  >;
  commandOptionType?: CommandOptionType;
};

export const AppRouteConfigs: { [key: string]: AppRouteConfigType } = {
  [MainRoute.Home]: {
    view: MainRoute.Home,
    path: '/home',
    label: <Content id={ContentId.Home} />,
    icon: HomeOutlineIcon,
    commandOptionType: CommandOptionType.NavigateHome,
  },
  [MainRoute.Inventory]: {
    view: MainRoute.Inventory,
    path: '/inventory',
    label: <Content id={ContentId.Inventory} />,
    icon: PaperTicketsOutlineIcon,
    subRoutes: {
      Search: { Path: '/search', Icon: SearchSolidIcon },
      SearchV2: { Path: '/search-v2', Icon: SearchSolidIcon },
    },
    favorite: UserSetting.InventoryMenuTabFavorite,
    commandOptionType: CommandOptionType.NavigateInventory,
  },
  [MainRoute.Sales]: {
    view: MainRoute.Sales,
    path: '/sales',
    label: (
      <DeviceAwareContentWrapper
        defaultId={ContentId.Sales}
        mobileId={ContentId.Sales}
      />
    ),
    icon: SaleAlertIcon,
    commandOptionType: CommandOptionType.NavigateSales,
    subRoutes: {
      Search: { Path: '/search', Icon: SearchSolidIcon },
      SearchV2: { Path: '/search-v2', Icon: SearchSolidIcon },
    },
    favorite: UserSetting.SaleMenuTabFavorite,
  },
  [MainRoute.Purchases]: {
    view: MainRoute.Purchases,
    path: '/purchases',
    label: <Content id={ContentId.Purchases} />,
    icon: SellOutlineIcon,
    commandOptionType: CommandOptionType.NavigatePurchases,
    subRoutes: {
      Search: { Path: '/search', Icon: SearchSolidIcon },
      SearchV2: { Path: '/search-v2', Icon: SearchSolidIcon },
      Create: { Path: '?uniquePurchaseId=0', Icon: PlusIcon },
    },
    favorite: UserSetting.PurchaseMenuTabFavorite,
  },
  [MainRoute.Payments]: {
    view: MainRoute.Payments,
    path: '/payments',
    label: <Content id={ContentId.Payments} />,
    icon: PaymentsIcon,
    commandOptionType: CommandOptionType.NavigatePayments,
  },
  [MainRoute.Accounting]: {
    view: MainRoute.Accounting,
    path: '/accounting',
    label: <Content id={ContentId.Accounting} />,
    icon: ReportsIcon,
  },
  [MainRoute.Reports]: {
    view: MainRoute.Reports,
    path: '/reports',
    label: <Content id={ContentId.Reports} />,
    icon: ReportsIcon,
    commandOptionType: CommandOptionType.NavigateReports,
  },
  [MainRoute.ReportsV2]: {
    view: MainRoute.ReportsV2,
    path: '/reports/v2',
    label: 'Reports V2', // no need to translate - once we release - we will need to use ContentId.Report again
    icon: ReportsIcon,
  },
  [MainRoute.Retail]: {
    view: MainRoute.Retail,
    path: '/retail',
    label: 'Retail',
    icon: CartOutlineIcon,
  },
  [MainRoute.SponsoredListings]: {
    view: MainRoute.SponsoredListings,
    path: '/sponsoredlistings',
    label: <Content id={ContentId.SponsoredListings} />,
    icon: BoostIconDisplay,
  },
  [MainRoute.Discovery]: {
    view: MainRoute.Discovery,
    path: '/discovery',
    label: <Content id={ContentId.EventDiscovery} />,
    icon: SearchSolidIcon,
  },
};

export const MenuItems: { [key: string]: AppRouteConfigType } = {
  [MainRoute.Email]: {
    view: MainRoute.Email,
    path: '/messages',
    label: <Content id={ContentId.Messages} />,
    icon: ChatIcon,
  },
  [MainRoute.ReachProSupport]: {
    view: MainRoute.ReachProSupport,
    path: '/reachpro-support',
    label: <Content id={ContentId.SupportCenter} />,
    icon: SellerTraderOutlineIcon,
  },
  [MainRoute.Settings]: {
    view: MainRoute.Settings,
    path: '/settings',
    label: <Content id={ContentId.Settings} />,
    icon: SettingsIcon,
    commandOptionType: CommandOptionType.NavigateSettings,
  },
} as const;

export const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: (
      <div>
        <RootErrorPage error={undefined} resetErrorBoundary={noop} />
      </div>
    ),
    children: [
      {
        path: '/',
        loader() {
          return redirect('/home');
        },
      },
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/login/callback',
        element: <LoginCallback />,
      },
      {
        path: '/logout',
        element: <Logout />,
      },
      {
        path: '/unauthorized',
        element: <LoginFailure />,
      },
      {
        path: AppRouteConfigs[MainRoute.Home].path,
        element: <Home />,
      },
      {
        path: AppRouteConfigs[MainRoute.Inventory].path,
        element: <Inventory />,
      },
      {
        path: '/inventory/event',
        element: withSuspense(LazyInventoryEvent),
      },
      {
        path: '/inventory/event/:eventId',
        element: withSuspense(LazyInventoryEventPageRedirect),
      },
      {
        path: '/inventory/search',
        element: withSuspense(LazyInventorySearch),
      },
      {
        path: '/inventory/search-v2',
        element: withSuspense(LazyInventorySearchV2),
      },
      {
        path: AppRouteConfigs[MainRoute.Discovery].path,
        element: withSuspense(LazyOnSaleDiscovery),
      },
      {
        path: '/discovery/event',
        element: withSuspense(LazyOnSaleEvent),
      },
      {
        path: AppRouteConfigs[MainRoute.Discovery].path,
        element: withSuspense(LazyOnSaleDiscovery),
      },
      {
        path: AppRouteConfigs[MainRoute.Sales].path,
        element: withSuspense(LazySales),
      },
      {
        path: '/sales/event',
        element: withSuspense(LazySalesEvent),
      },
      {
        path: '/sales/search',
        element: withSuspense(LazySaleSearch),
      },
      {
        path: '/sales/search-v2',
        element: withSuspense(LazySaleSearchV2),
      },
      {
        path: AppRouteConfigs[MainRoute.Purchases].path,
        element: withSuspense(LazyPurchases),
      },
      {
        path: AppRouteConfigs[MainRoute.Payments].path,
        element: withSuspense(LazyPayments),
      },
      {
        path: AppRouteConfigs[MainRoute.Accounting].path,
        element: withSuspense(LazyAccounting),
      },
      {
        path: '/purchases/event',
        element: withSuspense(LazyPurchaseEvent),
      },
      {
        path: '/purchases/search',
        element: withSuspense(LazyPurchaseSearch),
      },
      {
        path: '/purchases/search-v2',
        element: withSuspense(LazyPurchaseSearchV2),
      },
      {
        path: '/redirect-uk',
        element: withSuspense(LazyRedirectToStubHubUk),
      },
      {
        path: '/ticketutils',
        element: withSuspense(LazyTicketUtilsLanding),
      },
      {
        path: '/notifications',
        element: withSuspense(LazyNotifications),
      },
      {
        path: '/release-notes',
        element: withSuspense(LazyReleaseNotes),
      },
      {
        path: '/help',
        element: withSuspense(LazyHelpCenter),
      },
      {
        path: '/reports/v2',
        element: withSuspense(LazyReportsV2),
      },
      {
        path: AppRouteConfigs[MainRoute.Retail].path,
        element: withSuspense(LazyRetail),
      },
      {
        path: '/reports',
        element: withSuspense(LazyReports),
      },
      {
        path: '/reports/inventory/:reportId',
        element: withSuspense(LazyReportsInventory),
      },
      {
        path: '/reports/sale/:reportId',
        element: withSuspense(LazyReportsSale),
      },
      {
        path: '/reports/inventory/v2/:reportId',
        element: withSuspense(LazyReportsInventoryV2),
      },
      {
        path: '/reports/commission/v2/:reportId',
        element: withSuspense(LazyReportsCommission),
      },
      {
        path: '/reports/purchase/v2/:reportId',
        element: withSuspense(LazyReportsPurchase),
      },
      {
        path: '/reports/salelineitem/v2/:reportId',
        element: withSuspense(LazyReportsSaleLineItem),
      },
      {
        path: '/reports/soldticket/v2/:reportId',
        element: withSuspense(LazyReportsSoldTicket),
      },
      {
        path: '/reports/sale/v2/:reportId',
        element: withSuspense(LazyReportsSaleV2),
      },
      {
        path: '/reports/purchaseorderlineitem/v2/:reportId',
        element: withSuspense(LazyReportsPurchaseOrderLineItem),
      },
      {
        path: '/reports/costofgoodssold/v2/:reportId',
        element: withSuspense(LazyReportsCostOfGoodsSold),
      },
      {
        path: '/reports/salecostadjustments/v2/:reportId',
        element: withSuspense(LazyReportsSaleCostAdjustments),
      },
      {
        path: '/reports/ticketgroup/v2/:reportId',
        element: withSuspense(LazyReportsTicketGroup),
      },
      {
        path: '/activity',
        element: withSuspense(LazyActivity),
      },
      {
        path: '/sync-center',
        element: withSuspense(LazySyncCenter),
      },
      {
        path: AppRouteConfigs[MainRoute.SponsoredListings].path,
        element: withSuspense(LazySponsoredListings),
      },
      /**
       * Settings Routes
       */
      {
        path: '/settings',
        element: withSuspense(LazySettings),
        children: [
          {
            index: true,
            element: (
              <Navigate to={`/settings/${SettingTab.MyAccount}`} replace />
            ),
          },
          {
            path: SettingTab.MyAccount,
            element: withSuspense(LazySettingsViews.MyAccount),
          },
          {
            path: SettingTab.CurrencyConversion,
            element: withSuspense(LazySettingsViews.CurrencyConversion),
          },
          {
            path: SettingTab.FulfillmentProviders,
            element: withSuspense(LazySettingsViews.FulfillmentProviders),
          },
          {
            path: SettingTab.PurchaseOrder,
            element: withSuspense(LazySettingsViews.PurchaseOrderSetting),
          },
          {
            path: SettingTab.Inventory,
            element: withSuspense(LazySettingsViews.InventorySetting),
          },
          {
            path: SettingTab.Accounting,
            element: withSuspense(LazySettingsViews.AccountingSetting),
          },
          {
            path: SettingTab.UserManagement,
            element: withSuspense(LazySettingsViews.UsersManagement),
            children: [
              {
                path: '',
                element: (
                  <Stack direction="column" gap="l">
                    {withSuspense(LazySettingsViews.Users)}
                    {withSuspense(LazySettingsViews.Roles)}
                    {withSuspense(LazySettingsViews.Teams)}
                  </Stack>
                ),
              },
              {
                path: SettingTab.UserManagement_Users,
                element: withSuspense(LazySettingsViews.Users),
              },
              {
                path: SettingTab.UserManagement_Roles,
                element: withSuspense(LazySettingsViews.Roles),
              },
              {
                path: SettingTab.UserManagement_Teams,
                element: withSuspense(LazySettingsViews.Teams),
              },
            ],
          },
          {
            path: SettingTab.VendorManagement,
            element: withSuspense(LazySettingsViews.VendorManagement),
            children: [
              {
                path: '',
                element: (
                  <Stack direction="column" gap="l">
                    {withSuspense(LazySettingsViews.Payments)}
                    {withSuspense(LazySettingsViews.Vendors)}
                  </Stack>
                ),
              },
              {
                path: SettingTab.VendorManagement_Vendors,
                element: withSuspense(LazySettingsViews.Vendors),
              },
              {
                path: SettingTab.VendorManagement_Payments,
                element: withSuspense(LazySettingsViews.Payments),
              },
            ],
          },
          {
            path: SettingTab.SyncCenter,
            element: withSuspense(LazySettingsViews.SyncCenter),
          },
          {
            path: SettingTab.SellerAccount,
            element: withSuspense(LazySettingsViews.SellerAccount),
          },
          {
            path: SettingTab.AutoPricing,
            element: withSuspense(LazySettingsViews.AccountAutoPricingSettings),
          },
          {
            path: SettingTab.DialogSuppression,
            element: withSuspense(LazySettingsViews.DialogSuppression),
          },
          {
            path: SettingTab.EventDiscovery,
            element: withSuspense(LazySettingsViews.EventDiscovery),
          },
          {
            path: SettingTab.CustomTabs,
            element: withSuspense(LazySettingsViews.CustomTabsSettingsView),
          },
        ],
      },
      {
        path: '/custom-tab/:tabId',
        element: withSuspense(LazyCustomTabEmbedView),
      },
      {
        path: '/messages',
        element: withSuspense(LazyEmail),
        children: [
          {
            children: [
              { index: true },
              { path: 'support/:sellerSupportCaseId' },
              { path: ':inboundEmailId' },
            ],
            handle: {
              pageName: 'inbox',
            } satisfies EmailNavPageNameHandle,
          },
          {
            path: 'uncategorized',
            children: [{ index: true }, { path: ':inboundEmailId' }],
            handle: {
              pageName: 'uncategorized',
            } satisfies EmailNavPageNameHandle,
          },
          {
            path: 'recently-categorized',
            children: [{ index: true }, { path: ':inboundEmailId' }],
            handle: {
              pageName: 'recentlyCategorized',
            } satisfies EmailNavPageNameHandle,
          },
        ],
      },
      {
        path: '/reachpro-support',
        element: withSuspense(LazyReachProSupport),
        children: [{ index: true }, { path: ':reachProConversationId' }],
      },
    ],
  },
]);
